/**
 * Date and time formatting utilities
 * Backend sends UTC timestamps, frontend displays in local timezone
 */

const sqliteUTCDateTimePattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

const parseBackendDate = (dateString: string): Date | null => {
  const normalized = sqliteUTCDateTimePattern.test(dateString)
    ? `${dateString.replace(' ', 'T')}Z`
    : dateString;
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Formats a date string from backend (UTC) to local date string
 * Falls back to showing UTC value on error
 */
export const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = parseBackendDate(dateString);
    if (!date) return `${dateString} (UTC)`;
    
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return `${dateString} (UTC)`;
  }
};

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Formats a date string from backend (UTC) to local date and time: 2026-08-27 01:40:47.
 * Falls back to showing UTC value on error
 */
export const formatDateTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = parseBackendDate(dateString);
    if (!date) return `${dateString} (UTC)`;
    
    const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    return `${day} ${time}`;
  } catch {
    return `${dateString} (UTC)`;
  }
};

/**
 * Formats a backend timestamp as a UTC date and time: 2026-08-27 15:04:05.
 * Usage pages show one clock (UTC), so the graph and the tables agree.
 */
export const formatDateTimeUTC = (dateString: string | undefined | null): string => {
  if (!dateString) return '—';

  const date = parseBackendDate(dateString);
  if (!date) return `${dateString} (UTC)`;

  return date.toISOString().slice(0, 19).replace('T', ' ');
};

/**
 * Formats a date string from backend (UTC) to local time only
 * Falls back to showing UTC value on error
 */
export const formatTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = parseBackendDate(dateString);
    if (!date) return `${dateString} (UTC)`;
    
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return `${dateString} (UTC)`;
  }
};

export const formatSeconds = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—';

  const wholeSeconds = Math.floor(seconds);
  const minutes = Math.floor(wholeSeconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${wholeSeconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${wholeSeconds % 60}s`;
  }
  return `${wholeSeconds}s`;
};

export const formatDurationSeconds = (
  startDate: string | undefined | null,
  endDate: string | undefined | null
): string => {
  if (!startDate) return '-';
  if (!endDate) return `${startDate} (UTC)`;

  const parsedStart = parseBackendDate(startDate);
  const parsedEnd = parseBackendDate(endDate);

  if (!parsedStart) return `${startDate} (UTC)`;
  if (!parsedEnd) return `${startDate} - ${endDate} (UTC)`;

  return `${((parsedEnd.getTime() - parsedStart.getTime()) / 1000).toFixed(1)}s`;
};

/**
 * Formats duration between two dates in human-readable format
 * Falls back to showing raw timestamps on error
 */
export const formatDuration = (startDate: string | undefined | null, endDate?: string | undefined | null): string => {
  if (!startDate) return '-';
  
  try {
    const parsedStart = parseBackendDate(startDate);
    const parsedEnd = endDate ? parseBackendDate(endDate) : null;
    
    if (!parsedStart) return `${startDate} (UTC)`;
    if (endDate && !parsedEnd) return `${startDate} - ${endDate} (UTC)`;
    
    const start = parsedStart.getTime();
    const end = parsedEnd ? parsedEnd.getTime() : Date.now();
    const duration = end - start;
    return formatSeconds(duration / 1000);
  } catch {
    if (endDate) {
      return `${startDate} - ${endDate} (UTC)`;
    }
    return `${startDate} (UTC)`;
  }
};

/**
 * Gets relative time string (e.g., "2 hours ago", "in 5 minutes")
 * Falls back to showing UTC value on error
 */
export const getRelativeTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = parseBackendDate(dateString);
    if (!date) return `${dateString} (UTC)`;
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    // Use absolute value for calculations to fix Math.floor issues with negative numbers
    const absDiffMs = Math.abs(diffMs);
    const absDiffSec = Math.floor(absDiffMs / 1000);
    const absDiffMin = Math.floor(absDiffSec / 60);
    const absDiffHour = Math.floor(absDiffMin / 60);
    const absDiffDay = Math.floor(absDiffHour / 24);
    const absDiffWeek = Math.floor(absDiffDay / 7);
    const absDiffMonth = Math.floor(absDiffDay / 30);
    const absDiffYear = Math.floor(absDiffDay / 365);
    
    const isFuture = diffMs < 0;
    
    // For far future dates (> 2 years), just show the formatted date
    if (isFuture && absDiffYear > 2) {
      return formatDate(dateString);
    }
    
    // Handle yesterday and tomorrow specially
    if (absDiffDay === 1 && absDiffHour < 48) {
      // Check if it's actually yesterday/tomorrow (same time range)
      const dateMidnight = new Date(date);
      dateMidnight.setHours(0, 0, 0, 0);
      const nowMidnight = new Date(now);
      nowMidnight.setHours(0, 0, 0, 0);
      const dayDiff = Math.floor((nowMidnight.getTime() - dateMidnight.getTime()) / (1000 * 60 * 60 * 24));
      
      if (dayDiff === 1) return 'yesterday';
      if (dayDiff === -1) return 'tomorrow';
    }
    
    if (isFuture) {
      // Future date
      if (absDiffYear > 0) return `in ${absDiffYear} year${absDiffYear !== 1 ? 's' : ''}`;
      if (absDiffMonth > 0) return `in ${absDiffMonth} month${absDiffMonth !== 1 ? 's' : ''}`;
      if (absDiffWeek > 0) return `in ${absDiffWeek} week${absDiffWeek !== 1 ? 's' : ''}`;
      if (absDiffDay > 1) return `in ${absDiffDay} days`;
      if (absDiffDay === 1) return 'tomorrow';
      if (absDiffHour > 0) return `in ${absDiffHour} hour${absDiffHour !== 1 ? 's' : ''}`;
      if (absDiffMin > 0) return `in ${absDiffMin} minute${absDiffMin !== 1 ? 's' : ''}`;
      if (absDiffSec > 0) return `in ${absDiffSec} second${absDiffSec !== 1 ? 's' : ''}`;
      return 'in a moment';
    }
    
    // Past date
    if (absDiffYear > 0) return `${absDiffYear} year${absDiffYear !== 1 ? 's' : ''} ago`;
    if (absDiffMonth > 0) return `${absDiffMonth} month${absDiffMonth !== 1 ? 's' : ''} ago`;
    if (absDiffWeek > 0) return `${absDiffWeek} week${absDiffWeek !== 1 ? 's' : ''} ago`;
    if (absDiffDay > 1) return `${absDiffDay} days ago`;
    if (absDiffDay === 1) return 'yesterday';
    if (absDiffHour > 0) return `${absDiffHour} hour${absDiffHour !== 1 ? 's' : ''} ago`;
    if (absDiffMin > 0) return `${absDiffMin} minute${absDiffMin !== 1 ? 's' : ''} ago`;
    if (absDiffSec > 0) return `${absDiffSec} second${absDiffSec !== 1 ? 's' : ''} ago`;
    return 'just now';
  } catch {
    return `${dateString} (UTC)`;
  }
};
